import { queryOptions } from "@tanstack/react-query"
import type { AudienceEstimate,
  AutomationReport,
  CampaignReport,
  CampaignReportOption,
  SupportReport,
  AuditPage,
  CampaignDetail,
  CampaignResults,
  CampaignsPage,
  RecipientsPage,
  AutomationDetail,
  AutomationSummary,
  AutomationsPage,
  RunDetail,
  RunsPage,
  Template,
  ChannelConnectionDetail,
  ChannelsResponse,
  CommentsPage,
  ConnectionAttempt,
  ContactDetail,
  ContactImport,
  ContactsPage,
  ContactsStats,
  ConversationDetail,
  ConversationsPage,
  DataRequest,
  HandoverPage,
  ModerationPage,
  ModerationRule,
  OverviewMetrics,
  SavedReply,
  FieldDefinition,
  ImportRow,
  InvitationPreview,
  Me,
  NotificationPreferences,
  Permission,
  Role,
  Segment,
  Tag,
  TeamResponse,
  Workspace,
  CommentThread,
  InboxView,
  WorkItemsPage,
  WorkItemsQuery,
  NotificationsPage,
} from "@vienna/contracts"
import { api } from "./api"

export interface WorkspaceContext {
  workspace: Workspace
  role: Role
  isOwner: boolean
  permissions: Permission[]
}

export const meQuery = queryOptions({ queryKey: ["me"], queryFn: () => api<Me>("/me") })

/** Public: portal mode (the staff workspace is the front door) and where to send people back. */
export const configQuery = queryOptions({ queryKey: ["config"], queryFn: () => api<{ portalUrl: string | null }>("/config"), staleTime: Infinity })

export const workspaceQuery = (slug: string) =>
  queryOptions({ queryKey: ["workspace", slug], queryFn: () => api<WorkspaceContext>(`/w/${slug}`) })

export const teamQuery = (slug: string) =>
  queryOptions({ queryKey: ["workspace", slug, "team"], queryFn: () => api<TeamResponse>(`/w/${slug}/team`) })

export const notificationsQuery = (slug: string) =>
  queryOptions({ queryKey: ["workspace", slug, "notifications"], queryFn: () => api<NotificationPreferences>(`/w/${slug}/me/notifications`) })

export interface AuditFilters {
  q?: string
  actorUserId?: string
  category?: string
  from?: string
}

export const auditQuery = (slug: string, filters: AuditFilters) =>
  queryOptions({
    queryKey: ["workspace", slug, "audit", filters],
    queryFn: () => {
      const params = new URLSearchParams()
      for (const [k, v] of Object.entries(filters) as Array<[string, string | undefined]>) if (v) params.set(k, v)
      params.set("limit", "100")
      return api<AuditPage>(`/w/${slug}/audit?${params.toString()}`)
    },
  })

export const invitationPreviewQuery = (token: string) =>
  queryOptions({ queryKey: ["invitation", token], queryFn: () => api<InvitationPreview>(`/invitations/${token}`), retry: false })

export const LAST_WORKSPACE_KEY = "vienna:last-workspace"

export function rememberWorkspace(slug: string) {
  try {
    localStorage.setItem(LAST_WORKSPACE_KEY, slug)
  } catch {
    /* storage unavailable */
  }
}

export function lastWorkspace(): string | null {
  try {
    return localStorage.getItem(LAST_WORKSPACE_KEY)
  } catch {
    return null
  }
}

export const channelsQuery = (slug: string) =>
  queryOptions({ queryKey: ["workspace", slug, "channels"], queryFn: () => api<ChannelsResponse>(`/w/${slug}/channels`) })

export const connectionQuery = (slug: string, connectionId: string) =>
  queryOptions({ queryKey: ["workspace", slug, "channels", connectionId], queryFn: () => api<ChannelConnectionDetail>(`/w/${slug}/channels/${connectionId}`) })

export const attemptQuery = (slug: string, attemptId: string) =>
  queryOptions({ queryKey: ["workspace", slug, "channel-attempts", attemptId], queryFn: () => api<ConnectionAttempt>(`/w/${slug}/channels/attempts/${attemptId}`) })

// ---- Contacts (checkpoint 3) ----

export const contactsStatsQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "contacts", "stats"], queryFn: () => api<ContactsStats>(`/w/${slug}/contacts/stats`) })

export interface ContactFilters {
  q?: string
  tagId?: string
  permission?: string
  segmentId?: string
  cursor?: string
}

export const contactsQuery = (slug: string, filters: ContactFilters) =>
  queryOptions({
    queryKey: ["workspace", slug, "contacts", "list", filters],
    queryFn: () => {
      const params = new URLSearchParams()
      for (const [k, v] of Object.entries(filters) as Array<[string, string | undefined]>) if (v) params.set(k, v)
      return api<ContactsPage>(`/w/${slug}/contacts?${params.toString()}`)
    },
  })

export const contactQuery = (slug: string, contactId: string) => queryOptions({ queryKey: ["workspace", slug, "contacts", contactId], queryFn: () => api<ContactDetail>(`/w/${slug}/contacts/${contactId}`) })
export const tagsQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "tags"], queryFn: () => api<Tag[]>(`/w/${slug}/contacts/tags`) })
export const fieldsQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "fields"], queryFn: () => api<FieldDefinition[]>(`/w/${slug}/contacts/fields`) })
export const segmentsQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "segments"], queryFn: () => api<Segment[]>(`/w/${slug}/contacts/segments`) })
export const segmentQuery = (slug: string, segmentId: string) => queryOptions({ queryKey: ["workspace", slug, "segments", segmentId], queryFn: () => api<Segment>(`/w/${slug}/contacts/segments/${segmentId}`) })
export const importQuery = (slug: string, importId: string) => queryOptions({ queryKey: ["workspace", slug, "imports", importId], queryFn: () => api<ContactImport>(`/w/${slug}/contacts/imports/${importId}`) })
export const importRowsQuery = (slug: string, importId: string, decision?: string) =>
  queryOptions({ queryKey: ["workspace", slug, "imports", importId, "rows", decision ?? "all"], queryFn: () => api<{ rows: ImportRow[]; total: number }>(`/w/${slug}/contacts/imports/${importId}/rows${decision ? `?decision=${decision}` : ""}`) })
export const dataRequestsQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "data-requests"], queryFn: () => api<DataRequest[]>(`/w/${slug}/contacts/data-requests`) })

// ---- Inbox (checkpoint 4) ----

export const conversationsQuery = (slug: string, filters: { queue?: string; q?: string }) =>
  queryOptions({
    queryKey: ["workspace", slug, "conversations", "list", filters],
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters.queue) params.set("queue", filters.queue)
      if (filters.q) params.set("q", filters.q)
      return api<ConversationsPage>(`/w/${slug}/conversations?${params.toString()}`)
    },
  })
export const conversationQuery = (slug: string, conversationId: string) => queryOptions({ queryKey: ["workspace", slug, "conversations", conversationId], queryFn: () => api<ConversationDetail>(`/w/${slug}/conversations/${conversationId}`) })
export const handoversQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "handovers"], queryFn: () => api<HandoverPage>(`/w/${slug}/conversations/handovers`) })
export const commentsQuery = (slug: string, status?: string) => queryOptions({ queryKey: ["workspace", slug, "comments", status ?? "all"], queryFn: () => api<CommentsPage>(`/w/${slug}/comments${status ? `?status=${status}` : ""}`) })
export const commentThreadQuery = (slug: string, commentId: string) => queryOptions({ queryKey: ["workspace", slug, "comments", "thread", commentId], queryFn: () => api<CommentThread>(`/w/${slug}/comments/${commentId}`) })
/** The unified work-item index; `cursor` pages are fetched with useInfiniteQuery in the route. */
export const workItemsQuery = (slug: string, filters: WorkItemsQuery) => ({
  queryKey: ["workspace", slug, "inbox", "items", filters] as const,
  queryFn: ({ pageParam }: { pageParam: string | null }) => {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) if (v !== undefined && v !== "" && v !== null) params.set(k, String(v))
    if (pageParam) params.set("cursor", pageParam)
    return api<WorkItemsPage>(`/w/${slug}/inbox/items?${params.toString()}`)
  },
})
export const inboxViewsQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "inbox", "views"], queryFn: () => api<InboxView[]>(`/w/${slug}/inbox/views`) })
export const savedRepliesQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "saved-replies"], queryFn: () => api<SavedReply[]>(`/w/${slug}/saved-replies`) })
export const inboxNotificationsQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "notification-inbox"], queryFn: () => api<NotificationsPage>(`/w/${slug}/notifications`) })
export const moderationQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "moderation"], queryFn: () => api<ModerationPage>(`/w/${slug}/moderation`) })
export const moderationRuleQuery = (slug: string, ruleId: string) => queryOptions({ queryKey: ["workspace", slug, "moderation", ruleId], queryFn: () => api<ModerationRule>(`/w/${slug}/moderation/rules/${ruleId}`) })
export const overviewQuery = (slug: string, period: "7" | "30") => queryOptions({ queryKey: ["workspace", slug, "overview", period], queryFn: () => api<OverviewMetrics>(`/w/${slug}/overview?period=${period}`) })

// ---- Automations (checkpoint 5) ----

export const automationsQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "automations", "list"], queryFn: () => api<AutomationsPage>(`/w/${slug}/automations`) })
export const templatesQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "automations", "templates"], queryFn: () => api<Template[]>(`/w/${slug}/automations/templates`), staleTime: Infinity })
export const automationQuery = (slug: string, automationId: string) => queryOptions({ queryKey: ["workspace", slug, "automations", automationId], queryFn: () => api<AutomationDetail>(`/w/${slug}/automations/${automationId}`) })
export const runsQuery = (slug: string, filters: { automationId?: string; status?: string; q?: string }) =>
  queryOptions({
    queryKey: ["workspace", slug, "runs", filters],
    queryFn: () => {
      const params = new URLSearchParams()
      for (const [k, v] of Object.entries(filters) as Array<[string, string | undefined]>) if (v) params.set(k, v)
      return api<RunsPage>(`/w/${slug}/automations/runs?${params.toString()}`)
    },
  })
export const runQuery = (slug: string, runId: string) => queryOptions({ queryKey: ["workspace", slug, "runs", runId], queryFn: () => api<RunDetail>(`/w/${slug}/automations/runs/${runId}`) })
export const conversationAutomationsQuery = (slug: string, conversationId: string) => queryOptions({ queryKey: ["workspace", slug, "conversations", conversationId, "automations"], queryFn: () => api<AutomationSummary[]>(`/w/${slug}/conversations/${conversationId}/automations`) })

// ---- Campaigns (checkpoint 6) ----

export const campaignsQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "campaigns", "list"], queryFn: () => api<CampaignsPage>(`/w/${slug}/campaigns`) })
export const campaignQuery = (slug: string, campaignId: string) => queryOptions({ queryKey: ["workspace", slug, "campaigns", campaignId], queryFn: () => api<CampaignDetail>(`/w/${slug}/campaigns/${campaignId}`) })
export const campaignEstimateQuery = (slug: string, campaignId: string) => queryOptions({ queryKey: ["workspace", slug, "campaigns", campaignId, "estimate"], queryFn: () => api<AudienceEstimate>(`/w/${slug}/campaigns/${campaignId}/estimate`) })
export const campaignResultsQuery = (slug: string, campaignId: string) => queryOptions({ queryKey: ["workspace", slug, "campaigns", campaignId, "results"], queryFn: () => api<CampaignResults>(`/w/${slug}/campaigns/${campaignId}/results`) })
export const campaignRecipientsQuery = (slug: string, campaignId: string, filters: { outcome?: string; q?: string }) =>
  queryOptions({
    queryKey: ["workspace", slug, "campaigns", campaignId, "recipients", filters],
    queryFn: () => {
      const params = new URLSearchParams()
      for (const [k, v] of Object.entries(filters) as Array<[string, string | undefined]>) if (v) params.set(k, v)
      return api<RecipientsPage>(`/w/${slug}/campaigns/${campaignId}/recipients?${params.toString()}`)
    },
  })

// ---- Reports (checkpoint 7) ----

export const supportReportQuery = (slug: string, q: { period: string; channel: string }) => queryOptions({ queryKey: ["workspace", slug, "reports", "support", q], queryFn: () => api<SupportReport>(`/w/${slug}/reports/support?period=${q.period}&channel=${q.channel}`) })
export const automationReportQuery = (slug: string, q: { period: string; channel: string }) => queryOptions({ queryKey: ["workspace", slug, "reports", "automations", q], queryFn: () => api<AutomationReport>(`/w/${slug}/reports/automations?period=${q.period}&channel=${q.channel}`) })
export const campaignReportOptionsQuery = (slug: string) => queryOptions({ queryKey: ["workspace", slug, "reports", "campaign-options"], queryFn: () => api<CampaignReportOption[]>(`/w/${slug}/reports/campaigns`) })
export const campaignReportQuery = (slug: string, campaignId: string) => queryOptions({ queryKey: ["workspace", slug, "reports", "campaign", campaignId], queryFn: () => api<CampaignReport>(`/w/${slug}/reports/campaigns/${campaignId}`) })
